import React from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Box,
  Button,
  Card,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  Separator,
  Text,
  toastManager,
} from "@salestrak/ui";
import { useAuth } from "#app/lib/auth-context";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import { readJWT } from "#app/lib/jwt";
import { WorkspaceAvatar } from "#app/components/brand/workspace-avatar";
import { WorkspaceScreenLayout } from "#app/components/workspace-screen-layout";
import {
  normalizeIdentifierInput,
  toUrlFriendly,
  useWorkspaceCreate,
} from "#app/lib/hooks/use-workspace-create";

function WorkspaceCreateScreen() {
  const { state: authState } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = React.useState("");
  const [identifier, setIdentifier] = React.useState("");
  // Auto-derive the identifier from the name until the user edits it by hand;
  // clearing the field hands derivation back to the name.
  const [identifierDirty, setIdentifierDirty] = React.useState(false);

  const { urlPath, urlPathError, isCreating, createWorkspace, enterWorkspace } =
    useWorkspaceCreate(identifier);

  const userEmail = React.useMemo(() => {
    if (!authState.accessToken) return null;
    const payload = readJWT(authState.accessToken);
    return (payload.email as string) ?? null;
  }, [authState.accessToken]);

  const handleNameChange = React.useCallback(
    (value: string) => {
      setName(value);
      if (!identifierDirty) setIdentifier(toUrlFriendly(value));
    },
    [identifierDirty],
  );

  const handleIdentifierChange = React.useCallback((value: string) => {
    const normalized = normalizeIdentifierInput(value);
    setIdentifier(normalized);
    setIdentifierDirty(normalized.length > 0);
  }, []);

  const isValid =
    name.trim().length > 0 && urlPath.length > 0 && !urlPathError;

  const handleCreate = React.useCallback(async () => {
    if (!isValid || isCreating) return;
    try {
      const workspace = await createWorkspace(name, identifier);
      await enterWorkspace(workspace);
      navigate({ to: `/${workspace.urlPath}` as unknown as "/", replace: true });
      toastManager.add({ title: "Workspace created", type: "success" });
    } catch (err: any) {
      console.error("[WorkspaceCreate] Failed to create workspace", err);
      toastMutationError(err, "Failed to create workspace");
    }
  }, [
    isValid,
    isCreating,
    name,
    identifier,
    createWorkspace,
    enterWorkspace,
    navigate,
  ]);

  // A real form, so Enter in either field submits.
  const handleSubmit = React.useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      void handleCreate();
    },
    [handleCreate],
  );

  return (
    <WorkspaceScreenLayout email={userEmail}>
      {/* Box carries no margin props — block separation is bottom padding. */}
      <Box as="form" onSubmit={handleSubmit}>
        <Box paddingBottom="xs">
          <Text as="h3" size="2xl" weight="semibold">
            Create a new workspace
          </Text>
        </Box>

        <Box paddingBottom="lg">
          <Text size="sm" variant="muted">
            Workspaces are shared environments where teams and members can
            collaborate on different issues, collections and more.
          </Text>
        </Box>

        <Box paddingBottom="lg">
          <Card>
            <Box paddingX="xl" paddingTop="xl" paddingBottom="lg">
              <Box gap="md">
                <Text size="sm" weight="medium">
                  Name & Icon
                </Text>
                <Box direction="row" align="center" gap="sm">
                  {/* "D" keeps the placeholder tile from collapsing before the
                      first keystroke. */}
                  <WorkspaceAvatar name={name.trim() || "D"} size="default" />
                  <Box fill="flex">
                    <Input
                      placeholder="e.g. Development"
                      value={name}
                      onChange={(event) => handleNameChange(event.target.value)}
                      autoFocus
                    />
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box paddingX="xl">
              <Separator />
            </Box>

            <Box paddingX="xl" paddingTop="lg" paddingBottom="xl">
              <Field>
                <FieldLabel>Identifier</FieldLabel>
                <FieldDescription>
                  Unique key for this workspace — generated from the name,
                  editable
                </FieldDescription>
                <Input
                  placeholder="e.g. development"
                  value={identifier}
                  onChange={(event) =>
                    handleIdentifierChange(event.target.value)
                  }
                  autoCapitalize="none"
                  aria-invalid={!!urlPathError}
                />
                {urlPathError ? (
                  <Text size="xs" variant="destructive">
                    {urlPathError}
                  </Text>
                ) : urlPath ? (
                  <Text size="xs" variant="muted">
                    salestrak.io/{urlPath}
                  </Text>
                ) : null}
              </Field>
            </Box>
          </Card>
        </Box>

        <Box direction="row" align="center" justify="between">
          <Button
            type="button"
            variant="ghost"
            size="lg"
            onClick={() => navigate({ to: "/workspace-select", replace: true })}
          >
            Back
          </Button>
          <Button
            type="submit"
            size="lg"
            disabled={!isValid}
            loading={isCreating}
          >
            Create workspace
          </Button>
        </Box>
      </Box>
    </WorkspaceScreenLayout>
  );
}

export const Route = createFileRoute("/(app)/workspace-create")({
  component: WorkspaceCreateScreen,
});
